/**
 * UnifiedTimeline — chronological blend of Experience + Education
 * with tenure, gap/overlap detection and per-entry recruiter notes.
 *
 * Design:
 *   · One vertical timeline, newest first.
 *   · Experience rows use IKB blue; Education uses violet.
 *   · Tenure is computed from start/end dates. If end date is missing
 *     OR reads "present"/"till date"/"current" we treat it as ongoing
 *     and pin the tenure to *now* — the row shows a live "Present" pill.
 *   · Between consecutive entries we detect:
 *       – Gap  ≥ 30 days between one entry's end and the next's start.
 *       – Overlap  when a role's start predates the previous role's end.
 *     Either surfaces a callout row where a recruiter can leave a note
 *     explaining the anomaly (persisted in `timeline_notes`).
 *   · Per-entry comments — inline "Add note" toggle stores under
 *     ref_kind:experience|education, ref_key:<company or degree>.
 */
import { useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Briefcase, GraduationCap, AlertTriangle, CalendarClock, MessageSquare, Save, Sparkles, RefreshCw } from "lucide-react";
import { ExperienceDocsChip } from "@/components/candidate/ExperienceDocuments";

const PRESENT_KEYWORDS = ["present", "till date", "current", "now", "ongoing", "till now"];

function parseDate(s) {
    if (!s) return null;
    if (typeof s === "string" && PRESENT_KEYWORDS.some((k) => s.toLowerCase().includes(k))) return "present";
    const d = new Date(s);
    if (!isNaN(d.getTime())) return d;
    // Try MM/YYYY or YYYY
    const m = /^(\d{1,2})\/(\d{4})$/.exec(s);
    if (m) return new Date(+m[2], +m[1] - 1, 1);
    const y = /^(\d{4})$/.exec(s);
    if (y) return new Date(+y[1], 0, 1);
    return null;
}

function tenureString(start, end) {
    if (!start) return "";
    const endD = end === "present" ? new Date() : end;
    if (!endD) return "";
    const months = Math.max(0, (endD.getFullYear() - start.getFullYear()) * 12 + (endD.getMonth() - start.getMonth()));
    const y = Math.floor(months / 12);
    const m = months % 12;
    if (y === 0) return `${m || 1}mo`;
    if (m === 0) return `${y}y`;
    return `${y}y ${m}mo`;
}

function fmtMonth(d) {
    if (!d) return "—";
    if (d === "present") return "Present";
    return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

function refKey(entry, type) {
    return type === "experience"
        ? `${entry.company || ""}|${entry.title || ""}|${entry.start_date || ""}`
        : `${entry.institution || entry.school || ""}|${entry.degree || ""}|${entry.start_date || ""}`;
}

/** Merge experience + education, resolve dates, sort newest-first, and
 *  interleave gap/overlap synthetic rows between consecutive positions. */
function buildRows(experience = [], education = [], notesByKey = {}) {
    const rows = [];
    experience.forEach((e) => {
        const start = parseDate(e.start_date || e.start);
        const end = parseDate(e.end_date || e.end);
        if (!start) return;
        rows.push({
            type: "experience",
            entry: e,
            start,
            end: end || "present",  // treat missing end as ongoing
            hadExplicitEnd: !!(e.end_date || e.end),
            key: refKey(e, "experience"),
        });
    });
    education.forEach((e) => {
        const start = parseDate(e.start_date || e.start);
        const end = parseDate(e.end_date || e.end);
        if (!start) return;
        rows.push({
            type: "education",
            entry: e,
            start,
            end: end || "present",
            hadExplicitEnd: !!(e.end_date || e.end),
            key: refKey(e, "education"),
        });
    });
    // Sort newest first by end date. Within the SAME end-year, use the
    // start_date as a secondary tie-breaker (also descending) so a role that
    // started later in the year appears above an earlier-started role that
    // ended in the same year. This makes tightly-packed timelines
    // deterministic and easier to scan.
    rows.sort((a, b) => {
        const aE = a.end === "present" ? new Date() : a.end;
        const bE = b.end === "present" ? new Date() : b.end;
        const aYear = aE?.getFullYear?.() ?? -Infinity;
        const bYear = bE?.getFullYear?.() ?? -Infinity;
        if (aYear !== bYear) return bYear - aYear;
        // Same end year → tiebreak by start_date desc
        const aS = a.start?.getTime?.() || 0;
        const bS = b.start?.getTime?.() || 0;
        if (aS !== bS) return bS - aS;
        // Final fallback: full end timestamp desc
        return (bE?.getTime?.() || 0) - (aE?.getTime?.() || 0);
    });
    // Attach notes.
    rows.forEach((r) => { r.note = notesByKey[`${r.type}|${r.key}`]?.note || ""; });

    // Interleave gap/overlap markers between adjacent rows (older→newer window).
    const merged = [];
    for (let i = 0; i < rows.length; i++) {
        merged.push(rows[i]);
        const cur = rows[i];
        const next = rows[i + 1];
        if (!next) continue;
        // Only compare when both have real dates.
        const curStart = cur.start;
        const nextEnd = next.end === "present" ? new Date() : next.end;
        if (!curStart || !nextEnd) continue;
        const diffDays = Math.round((curStart - nextEnd) / (1000 * 60 * 60 * 24));
        if (diffDays >= 30) {
            const gapKey = `gap:${nextEnd.toISOString().slice(0, 10)}-${curStart.toISOString().slice(0, 10)}`;
            merged.push({
                type: "gap",
                key: gapKey,
                start: nextEnd,
                end: curStart,
                days: diffDays,
                note: notesByKey[`gap|${gapKey}`]?.note || "",
            });
        } else if (diffDays < -30) {
            const overlapKey = `overlap:${curStart.toISOString().slice(0, 10)}-${nextEnd.toISOString().slice(0, 10)}`;
            merged.push({
                type: "overlap",
                key: overlapKey,
                start: curStart,
                end: nextEnd,
                days: -diffDays,
                note: notesByKey[`gap|${overlapKey}`]?.note || "",  // stored under 'gap' kind for simplicity
            });
        }
    }
    return merged;
}

export default function UnifiedTimeline({ candidate, onNotesRefresh }) {
    const [editingKey, setEditingKey] = useState(null);
    const [draft, setDraft] = useState("");
    const [saving, setSaving] = useState(false);

    const notesByKey = useMemo(() => {
        const map = {};
        (candidate?.timeline_notes || []).forEach((n) => { map[`${n.ref_kind}|${n.ref_key}`] = n; });
        return map;
    }, [candidate]);

    const rows = useMemo(() => buildRows(candidate?.experience, candidate?.education, notesByKey), [candidate, notesByKey]);

    async function saveNote(refKind, key, existingNote) {
        setSaving(true);
        try {
            await api.post(`/candidates/${candidate.id}/timeline-note`, {
                ref_kind: refKind,
                ref_key: key,
                note: draft.trim(),
            });
            toast.success(draft.trim() ? "Note saved" : "Note cleared");
            setEditingKey(null);
            setDraft("");
            onNotesRefresh?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    }

    // Don't bail early on empty rows — we still want to surface the
    // AI Career Narrative for candidates whose experience/education is
    // present but undated (common with partial resume parses). The
    // narrative summarises raw text; the visual timeline degrades to
    // a soft empty state.
    const hasSummarisable = (candidate?.experience?.length || 0) > 0 || (candidate?.education?.length || 0) > 0;
    if (!hasSummarisable) return null;

    return (
        <section data-testid="unified-timeline">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
                <h3 className="font-display font-bold text-sm uppercase tracking-widest text-foreground">Career + education timeline</h3>
                <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">chronological · newest first</span>
                <span className="ml-auto flex items-center gap-3 text-[10px] font-mono">
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-ikb inline-block" /> experience</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-violet-600 inline-block" /> education</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-amber-500 inline-block" /> gap/overlap</span>
                </span>
            </div>
            <CareerNarrativeBlurb candidate={candidate} onNotesRefresh={onNotesRefresh} />
            {rows.length === 0 ? (
                <div className="border border-dashed border-border p-4 text-xs text-muted-foreground text-center" data-testid="unified-timeline-empty">
                    Experience or education is on file, but none of the entries have parseable dates yet.
                    Add start/end dates to any role to see the visual timeline.
                </div>
            ) : (
            <ol className="border-l-2 border-neutral-200 pl-4 space-y-3 relative">
                {rows.map((r, idx) => {
                    if (r.type === "gap" || r.type === "overlap") {
                        return <GapRow key={r.key} row={r} onEdit={() => { setEditingKey(r.key); setDraft(r.note || ""); }} editing={editingKey === r.key} draft={draft} setDraft={setDraft} onSave={() => saveNote("gap", r.key, r.note)} saving={saving} />;
                    }
                    const isExp = r.type === "experience";
                    const e = r.entry;
                    const title = isExp ? (e.title || "Untitled role") : (e.degree || "Course");
                    const org = isExp ? (e.company || "") : (e.institution || e.school || "");
                    const isPresent = r.end === "present";
                    const tenure = tenureString(r.start, r.end);
                    return (
                        <li key={idx} className={`relative ${isExp ? "" : ""}`} data-testid={`timeline-row-${idx}`}>
                            <span className={`absolute -left-[22px] top-2 w-3 h-3 rounded-full ${isExp ? "bg-ikb" : "bg-violet-600"} ring-2 ring-white`} />
                            <div className={`border border-border bg-white p-3 ${isExp ? "border-l-4 border-l-ikb" : "border-l-4 border-l-violet-600"}`}>
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {isExp ? <Briefcase className="w-3.5 h-3.5 text-ikb" /> : <GraduationCap className="w-3.5 h-3.5 text-violet-600" />}
                                            <span className="font-semibold text-sm">{title}</span>
                                            {isPresent && <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-emerald-500 text-emerald-700 bg-emerald-50">Present</span>}
                                        </div>
                                        {org && <div className="text-xs text-muted-foreground mt-0.5">{org}</div>}
                                        {isExp && e.description && <div className="text-[11px] text-foreground/80 mt-1 leading-snug line-clamp-3">{e.description}</div>}
                                        {/* Employment documents chip — inline compact
                                         *  quick-access. Full editor lives in the
                                         *  Employment Documents card below the timeline. */}
                                        {isExp && (
                                            <div className="mt-1.5">
                                                <ExperienceDocsChip
                                                    candidateId={candidate.id}
                                                    experience={e}
                                                    bundle={(candidate.experience_documents || {})[r.key.replace(/^experience:/, "")] || (candidate.experience_documents || {})[`${e.company || ""}|${e.title || ""}|${e.start_date || e.start || ""}`]}
                                                    onRefresh={onNotesRefresh}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{fmtMonth(r.start)} → {fmtMonth(r.end)}</div>
                                        {tenure && <div className="text-[11px] font-mono text-ikb mt-0.5">{tenure}</div>}
                                    </div>
                                </div>
                                {/* Recruiter note */}
                                {r.note && editingKey !== r.key && (
                                    <div className="mt-2 pt-2 border-t border-dashed border-border text-[11px] text-foreground/80 italic flex items-start gap-1.5">
                                        <MessageSquare className="w-3 h-3 shrink-0 mt-0.5 text-ikb" />
                                        <span>"{r.note}"</span>
                                    </div>
                                )}
                                {editingKey === r.key ? (
                                    <div className="mt-2 pt-2 border-t border-dashed border-border space-y-2">
                                        <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} placeholder="Comment on this position…" className="w-full border border-border text-xs px-2 py-1" data-testid={`timeline-note-input-${idx}`} />
                                        <div className="flex items-center gap-2 justify-end">
                                            <button onClick={() => { setEditingKey(null); setDraft(""); }} className="h-7 px-2 border border-border text-[10px] font-mono uppercase tracking-widest">Cancel</button>
                                            <button onClick={() => saveNote(r.type, r.key, r.note)} disabled={saving} className="h-7 px-2 border border-ikb bg-ikb text-white text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1" data-testid={`timeline-note-save-${idx}`}>
                                                <Save className="w-3 h-3" /> Save
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    !r.note && (
                                        <button onClick={() => { setEditingKey(r.key); setDraft(""); }} className="mt-2 text-[10px] font-mono uppercase tracking-widest text-ikb hover:underline inline-flex items-center gap-1" data-testid={`timeline-note-add-${idx}`}>
                                            <MessageSquare className="w-3 h-3" /> Add note
                                        </button>
                                    )
                                )}
                            </div>
                        </li>
                    );
                })}
            </ol>
            )}
        </section>
    );
}

function GapRow({ row, onEdit, editing, draft, setDraft, onSave, saving }) {
    const isGap = row.type === "gap";
    const months = Math.round(row.days / 30);
    return (
        <li className="relative" data-testid={`timeline-${row.type}-${row.key}`}>
            <span className="absolute -left-[22px] top-2 w-3 h-3 rounded-full bg-amber-500 ring-2 ring-white" />
            <div className="border border-amber-300 bg-amber-50/60 p-2">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="inline-flex items-center gap-2 text-xs text-amber-900">
                        {isGap ? <CalendarClock className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                        <span className="font-mono uppercase tracking-widest text-[10px]">{isGap ? "Gap" : "Overlap"}</span>
                        <span>{fmtMonth(row.start)} → {fmtMonth(row.end)} · {months}mo</span>
                    </div>
                    {!editing && (
                        <button onClick={onEdit} className="text-[10px] font-mono uppercase tracking-widest text-amber-900 hover:underline">
                            {row.note ? "Edit note" : "Explain"}
                        </button>
                    )}
                </div>
                {row.note && !editing && <div className="text-[11px] text-amber-900 italic mt-1">"{row.note}"</div>}
                {editing && (
                    <div className="mt-2 space-y-2">
                        <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} placeholder={isGap ? "Explain the gap (e.g., sabbatical, care-giving, freelance)…" : "Explain the overlap (e.g., freelance while employed)…"} className="w-full border border-amber-300 bg-white text-xs px-2 py-1" />
                        <div className="flex items-center gap-2 justify-end">
                            <button onClick={onEdit} className="h-7 px-2 border border-border text-[10px] font-mono uppercase tracking-widest">Cancel</button>
                            <button onClick={onSave} disabled={saving} className="h-7 px-2 border border-amber-600 bg-amber-500 text-white text-[10px] font-mono uppercase tracking-widest">Save</button>
                        </div>
                    </div>
                )}
            </div>
        </li>
    );
}


/**
 * CareerNarrativeBlurb — LLM-generated 2-3 sentence recruiter-facing story
 * distilled from the candidate's unified timeline. Lives right at the top
 * of the timeline card so recruiters get the shape of the career before
 * they scroll through individual rows.
 *
 * Cached on `candidate.career_narrative` and re-generated on demand via
 * POST /candidates/{cid}/career-narrative. Empty state shows a generate
 * button; populated state shows the story plus a subtle "regenerate"
 * chip so recruiters can refresh after edits to experience/education.
 */
function CareerNarrativeBlurb({ candidate, onNotesRefresh }) {
    const [busy, setBusy] = useState(false);
    const narrative = candidate?.career_narrative || "";
    const generatedAt = candidate?.career_narrative_generated_at;
    const hasExp = (candidate?.experience?.length || 0) > 0 || (candidate?.education?.length || 0) > 0;
    if (!hasExp) return null;

    const generate = async () => {
        setBusy(true);
        try {
            await api.post(`/candidates/${candidate.id}/career-narrative`);
            toast.success(narrative ? "Career narrative refreshed" : "Career narrative generated");
            onNotesRefresh?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    if (!narrative) {
        return (
            <div className="border border-dashed border-violet-300 bg-violet-50/40 p-3 mb-3 flex items-center justify-between gap-3 flex-wrap" data-testid="career-narrative-empty">
                <div className="flex items-center gap-2 text-xs text-violet-900">
                    <Sparkles className="w-3.5 h-3.5" />
                    <span>Generate an AI career narrative — a 2-3 sentence recruiter-facing story from this timeline.</span>
                </div>
                <button
                    type="button"
                    onClick={generate}
                    disabled={busy}
                    className="h-8 px-3 border border-violet-600 bg-violet-600 text-white text-[10px] font-mono uppercase tracking-widest hover:bg-violet-700 disabled:opacity-60 inline-flex items-center gap-1.5"
                    data-testid="career-narrative-generate-btn"
                >
                    <Sparkles className="w-3 h-3" /> {busy ? "Generating…" : "Generate narrative"}
                </button>
            </div>
        );
    }

    return (
        <div className="border border-violet-200 bg-violet-50/60 p-3 mb-3" data-testid="career-narrative">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-violet-800">
                    <Sparkles className="w-3 h-3" /> AI Career Narrative
                    {generatedAt && <span className="text-violet-700/70 normal-case">· {timeAgoShort(generatedAt)}</span>}
                </div>
                <button
                    type="button"
                    onClick={generate}
                    disabled={busy}
                    className="h-6 px-2 border border-violet-300 bg-white text-[9px] font-mono uppercase tracking-widest text-violet-800 hover:bg-violet-100 disabled:opacity-60 inline-flex items-center gap-1"
                    data-testid="career-narrative-regenerate-btn"
                    title="Regenerate from the latest timeline"
                >
                    <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> {busy ? "…" : "Refresh"}
                </button>
            </div>
            <p className="text-sm text-violet-950 mt-2 leading-relaxed" data-testid="career-narrative-text">{narrative}</p>
        </div>
    );
}

function timeAgoShort(iso) {
    try {
        const d = new Date(iso);
        const diff = Math.floor((Date.now() - d.getTime()) / 1000);
        if (diff < 60) return "just now";
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
        return d.toLocaleDateString();
    } catch { return ""; }
}
